import React from 'react'
import { Route, useRouteMatch } from 'react-router'
import { positionDefault } from '@reach/popover'

// Utils
import { routes, useFlag } from '@sketch/modules-common'
import { useSearch } from 'modules/shares/hooks/useSearch'

// Components
import {
  Tooltip,
  TooltipShortcut,
  Dropdown,
  SelectInput,
  SelectTriggerUnstyled,
} from '@sketch/components'
import SearchBarWithFilters, {
  getPlaceholderByPath,
} from './SearchBarWithFilters'

// Styles
import {
  Filter,
  IconWrapper,
  StyledCheckbox,
  StyledChevron,
  StyledSelectPopover,
  ToggleWrapper,
  StyledDropdownItem,
} from './SearchBar.styles'

// GraphQL
import {
  useGetDocumentListSettingsQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

export interface SearchBarProps {
  userRole: WorkspaceMinimalFragment['userRole']
}

const SearchBarWithoutFilters = ({ userRole }: SearchBarProps) => {
  const { data, updateQuery } = useGetDocumentListSettingsQuery()
  const { showDocumentsInProjects } = data || {}
  const { search, setSearch } = useSearch()

  const shouldShowSearchOptions = userRole !== 'GUEST'

  const { path } = useRouteMatch()
  const inputPlaceholder = getPlaceholderByPath(path)

  return (
    <Tooltip
      content={
        <>
          Search
          <TooltipShortcut>Press /</TooltipShortcut>
        </>
      }
      disableWhenTouchDevice
      placement="bottom-start"
    >
      <Filter
        onChange={setSearch}
        value={search}
        name="search"
        placeholder={inputPlaceholder}
      >
        <Route path={routes.WORKSPACE_SHARES.template()}>
          {shouldShowSearchOptions && (
            <SelectInput value={'Options'}>
              <SelectTriggerUnstyled>
                <ToggleWrapper>
                  Options
                  <IconWrapper>
                    <StyledChevron />
                  </IconWrapper>
                </ToggleWrapper>
              </SelectTriggerUnstyled>
              <StyledSelectPopover position={positionDefault}>
                <Dropdown.Header>Show Documents</Dropdown.Header>
                <StyledDropdownItem>
                  <StyledCheckbox
                    checked={showDocumentsInProjects}
                    label="Include documents in Projects"
                    onChange={event =>
                      updateQuery(previous => ({
                        ...previous,
                        showDocumentsInProjects: !showDocumentsInProjects,
                      }))
                    }
                  />
                </StyledDropdownItem>
              </StyledSelectPopover>
            </SelectInput>
          )}
        </Route>
      </Filter>
    </Tooltip>
  )
}

export const SearchBar = ({ userRole }: SearchBarProps) => {
  const isSearchFilterSortEnabled = useFlag('search-filter-sort')

  if (isSearchFilterSortEnabled) {
    return <SearchBarWithFilters userRole={userRole} />
  }

  return <SearchBarWithoutFilters userRole={userRole} />
}
